import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Badge, Btn, Modal, Loading, EmptyState, KpiCard, SectionLabel } from '../components/ui';

const API = `${window.location.protocol}//${window.location.hostname}/api`;

function authHeaders() {
  const token = localStorage.getItem('lc_token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' };
}

function skillBadgeType(pct) {
  if (pct >= 80) return 'green';
  if (pct >= 60) return 'amber';
  return 'red';
}

function tierBadge(tier) {
  if (tier === 'operation') return <Badge type="blue" size="xs">O</Badge>;
  if (tier === 'fund') return <Badge type="amber" size="xs">F</Badge>;
  if (tier === 'any')  return <Badge type="gray"  size="xs">A</Badge>;
  return null;
}

function tierBorderColor(slot) {
  if (slot.is_vacant || !slot.employee_id) return '2px dashed #A32D2D';
  if (slot.match_tier === 'direct') return '2px solid #3B6D11';
  if (slot.match_tier === 'operation') return '2px solid #185FA5';
  if (slot.match_tier === 'fund')   return '2px solid #854F0B';
  return '2px solid rgba(0,0,0,0.2)';
}

function groupSlots(slots) {
  const map = {};
  for (const s of slots) {
    const key = s.line_design_item_id;
    if (!map[key]) {
      map[key] = {
        line_design_item_id: s.line_design_item_id,
        operation_name: s.operation_name,
        sam: s.sam,
        skill_req_pct: s.skill_req_pct,
        operators_count: s.operators_count,
        output_per_hr: s.output_per_hr,
        is_bottleneck: s.is_bottleneck,
        machine_type: s.machine_type,
        op_row: s.op_row,
        is_clubbed: false,
        clubbed_operation_name: null,
        slots: []
      };
    }
    if (s.is_clubbed) {
      map[key].is_clubbed = true;
      map[key].clubbed_operation_name = s.clubbed_operation_name;
    }
    map[key].slots.push(s);
  }
  return Object.values(map).sort((a, b) => b.skill_req_pct - a.skill_req_pct || a.op_row - b.op_row);
}

// A shared (clubbed) slot's operator only spends half their time on this specific
// operation — count it as 0.5 capacity, not a full 1.0, for output/efficiency math.
function slotWeight(slot) { return slot.is_clubbed ? 0.5 : 1; }

function calcEfficiency(operations, totalSam, opMins) {
  const mins = opMins || 480;
  const sam = parseFloat(totalSam) || 0;
  if (!sam) return 0;
  const opOutputs = [];
  for (const op of operations) {
    const presentSlots = op.slots.filter(s => s.employee_id && !s.is_absent);
    if (presentSlots.length === 0) return 0;
    const weight = presentSlots.reduce((a, s) => a + slotWeight(s), 0);
    const weightedSkillSum = presentSlots.reduce((a, s) => a + (parseFloat(s.skill_pct) || 0) * slotWeight(s), 0);
    const avgSkill = weight > 0 ? weightedSkillSum / weight : 0;
    if (avgSkill === 0) return 0;
    const effCT = parseFloat(op.sam) / (avgSkill / 100);
    opOutputs.push((weight * 60) / effCT);
  }
  if (!opOutputs.length) return 0;
  const targetDay = Math.min(...opOutputs) * (mins / 60);
  // Denominator is the STATIC Line Design operator total, matching the same
  // reference point LD's own efficiency uses — not a recount of who's actually
  // deployed. A shared operator being "1 person, 2 rows" is already accounted
  // for in the LD's own operators_count math (0.5 + 0.5 = 1 person).
  const totalPresent = operations.reduce((s, op) => s + (parseFloat(op.operators_count) || 0), 0);
  if (!totalPresent) return 0;
  return Math.round((targetDay * sam) / (totalPresent * mins) * 1000) / 10;
}

function calcBottleneck(operations) {
  let minOutput = Infinity;
  let bottleneckName = null;
  for (const op of operations) {
    const presentSlots = op.slots.filter(s => s.employee_id && !s.is_absent);
    if (!presentSlots.length) return op.operation_name;
    const weight = presentSlots.reduce((a, s) => a + slotWeight(s), 0);
    const weightedSkillSum = presentSlots.reduce((a, s) => a + (parseFloat(s.skill_pct) || 0) * slotWeight(s), 0);
    const avgSkill = weight > 0 ? weightedSkillSum / weight : 0;
    if (avgSkill === 0) return op.operation_name;
    const effCT = parseFloat(op.sam) / (avgSkill / 100);
    const outputHr = (weight * 60) / effCT;
    if (outputHr < minOutput) { minOutput = outputHr; bottleneckName = op.operation_name; }
  }
  return bottleneckName;
}

// ─── Line Selector Landing ────────────────────────────────────────────────────
function LineSelector({ deployments, onSelect, error }) {
  const [search, setSearch] = useState('');
  const [shiftFilter, setShiftFilter] = useState('');

  const shiftOptions = [...new Set(deployments.map(d => d.shift_name).filter(Boolean))].sort();

  const filtered = deployments.filter(d =>
    (String(d.line_number).toLowerCase().includes(search.toLowerCase()) ||
    d.style_number?.toLowerCase().includes(search.toLowerCase()) ||
    d.buyer?.toLowerCase().includes(search.toLowerCase())) &&
    (!shiftFilter || d.shift_name === shiftFilter)
  );

  return (
    <div style={{ padding: 16, background: '#EFEDE8', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>Daily Balancing</div>
        <div style={{ fontSize: 12, color: '#666666', marginTop: 2 }}>
          Select a line to begin
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ background: '#FCEBEB', border: '1px solid #A32D2D', borderRadius: 7, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#A32D2D' }}>
          {error}
        </div>
      )}

      {/* Search bar + Shift filter */}
      <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ flex: '1 1 200px', position: 'relative' }}>
          <span style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            fontSize: 14, color: '#666666', pointerEvents: 'none'
          }}>🔍</span>
          <input
            className="inp"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by line number, style or buyer…"
            style={{ width: '100%', paddingLeft: 34, boxSizing: 'border-box' }}
          />
        </div>
        <select value={shiftFilter} onChange={e => setShiftFilter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 6, border: '1.5px solid rgba(0,0,0,0.15)', background: '#fff', fontSize: 13, cursor: 'pointer' }}>
          <option value="">All shifts</option>
          {shiftOptions.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Line cards */}
      {filtered.length === 0 && (
        <EmptyState message={deployments.length === 0 ? "No active deployments found. Activate a deployment first." : "No lines match your search."} />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {filtered.map(d => (
          <div key={d.id} onClick={() => onSelect(String(d.id))} style={{
            background: '#fff', borderRadius: 10, padding: '16px 18px',
            border: '0.5px solid rgba(0,0,0,0.10)', cursor: 'pointer',
            transition: 'box-shadow 0.15s',
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
          }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.10)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Line {d.line_number}</div>
              <Badge type="green">Active</Badge>
            </div>
            <div style={{ fontSize: 13, color: '#1a1a1a', marginBottom: 4 }}>
              {d.style_number}
              {d.buyer ? ` — ${d.buyer}` : ''}
            </div>
            <div style={{ fontSize: 11, color: '#666666' }}>
              {d.shift_name} · {d.manpower || '—'} operators
            </div>
            <div style={{
              marginTop: 12, paddingTop: 10, borderTop: '0.5px solid rgba(0,0,0,0.10)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>
              <span style={{ fontSize: 12, color: '#185FA5', fontWeight: 600 }}>
                {Math.round(d.plan_efficiency || d.efficiency || 0)}% efficiency
              </span>
              <span style={{ fontSize: 12, color: '#666666' }}>Open →</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Assign Modal ─────────────────────────────────────────────────────────────
function AssignModal({ open, onClose, deploymentId, ldiId, slotId, opName, isClubbed, clubbedOpName, clubbedLineNumber, currentLineNumber, onAssigned }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assignError, setAssignError] = useState('');
  const [pendingCandidate, setPendingCandidate] = useState(null);

  const isCrossLine = isClubbed && clubbedLineNumber != null && String(clubbedLineNumber) !== String(currentLineNumber);

  useEffect(() => {
    if (!open || !ldiId) return;
    setLoading(true);
    setAssignError('');
    setPendingCandidate(null);
    fetch(`${API}/daily-balancing/${deploymentId}/candidates?line_design_item_id=${ldiId}&slot_id=${slotId}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setCandidates(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [open, ldiId, slotId, deploymentId]);

  const doAssign = async (c, syncPartner) => {
    setAssignError('');
    const r = await fetch(`${API}/daily-balancing/${deploymentId}/slots/${slotId}`, {
      method: 'PUT', headers: authHeaders(),
      body: JSON.stringify({ employee_id: c.employee_id, match_tier: c.tier, skill_pct: c.skill_pct, sync_partner: syncPartner })
    });
    const data = await r.json();
    if (!r.ok) { setAssignError(data.error || 'Assignment failed'); return; }
    onAssigned(); onClose();
  };

  const handleCandidateClick = (c) => {
    if (isCrossLine) { setPendingCandidate(c); return; }
    doAssign(c, true);
  };

  const remove = async () => {
    await fetch(`${API}/daily-balancing/${deploymentId}/slots/${slotId}`, {
      method: 'PUT', headers: authHeaders(),
      body: JSON.stringify({ employee_id: null, match_tier: null, skill_pct: 0 })
    });
    onAssigned(); onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title={`Assign — ${opName}`} width={460}>
      {loading ? <Loading /> : <>
        {assignError && (
          <div style={{ background: '#FCEBEB', border: '1px solid #A32D2D', borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 12, color: '#A32D2D' }}>
            {assignError}
          </div>
        )}
        {pendingCandidate ? (
          <div style={{ padding: '12px 14px', background: '#E6F1FB', borderRadius: 8, marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{pendingCandidate.emp_name}</div>
            <div style={{ fontSize: 12, color: '#666666', marginBottom: 10 }}>
              This slot shares an operator with <strong>{clubbedOpName}</strong> on <strong>Line {clubbedLineNumber}</strong>.
              Does {pendingCandidate.emp_name.split(' ')[0]} cover both lines, or only this one?
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Btn variant="primary" size="sm" onClick={() => doAssign(pendingCandidate, true)}>
                Both lines
              </Btn>
              <Btn size="sm" onClick={() => doAssign(pendingCandidate, false)}>
                Only Line {currentLineNumber}
              </Btn>
              <Btn size="sm" onClick={() => setPendingCandidate(null)}>Cancel</Btn>
            </div>
          </div>
        ) : (
          <>
            {candidates.length === 0 && <EmptyState message="No available candidates. All operators are deployed in this unit." />}
            {candidates.map((c, i) => (
              <div key={c.employee_id} onClick={() => handleCandidateClick(c)} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px',
                borderRadius: 7, border: '0.5px solid rgba(0,0,0,0.08)', marginBottom: 5,
                cursor: 'pointer', background: i === 0 ? '#EAF3DE' : '#fff'
              }}>
                {i === 0 && <Badge type="green" size="xs">Best match</Badge>}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{c.emp_name}</div>
                  <div style={{ fontSize: 11, color: '#666666' }}>{c.emp_id} · Line {c.line_number}</div>
                </div>
                <Badge type={c.tier === 'direct' ? 'green' : c.tier === 'operation' ? 'blue' : c.tier === 'fund' ? 'amber' : 'gray'} size="xs">
                  {c.tier === 'direct' ? 'Direct' : c.tier === 'operation' ? 'Op.' : c.tier === 'fund' ? 'Fund.' : 'Any'}
                </Badge>
                <Badge type={skillBadgeType(c.skill_pct)}>{c.skill_pct}%</Badge>
              </div>
            ))}
          </>
        )}
        <div style={{ borderTop: '0.5px solid rgba(0,0,0,0.10)', paddingTop: 10, marginTop: 6 }}>
          <Btn variant="default" size="sm" onClick={remove}>Remove assignment</Btn>
        </div>
      </>}
    </Modal>
  );
}

// ─── Pool Panel ───────────────────────────────────────────────────────────────
function PoolPanel({ deploymentId, onReleaseElsewhere }) {
  const [poolData, setPoolData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchCode, setSearchCode] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [releasing, setReleasing] = useState(null);

  const fetchPool = useCallback(() => {
    if (!deploymentId) return;
    setLoading(true);
    fetch(`${API}/daily-balancing/${deploymentId}/pool`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setPoolData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [deploymentId]);

  useEffect(() => { fetchPool(); }, [fetchPool]);

  const handleSearch = async () => {
    if (!searchCode.trim()) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const r = await fetch(`${API}/daily-balancing/${deploymentId}/search-employee?code=${encodeURIComponent(searchCode.trim())}`, { headers: authHeaders() });
      const d = await r.json();
      setSearchResults(d.results || []);
    } catch (e) { setSearchResults([]); }
    setSearching(false);
  };

  const handleRelease = async (result) => {
    if (!window.confirm(`Release ${result.name} from Line ${result.assignment.line_number} · ${result.assignment.operation_name}?`)) return;
    setReleasing(result.id);
    try {
      await fetch(`${API}/daily-balancing/${result.assignment.deployment_id}/slots/${result.assignment.slot_id}`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ employee_id: null })
      });
      await handleSearch();
      fetchPool();
      onReleaseElsewhere && onReleaseElsewhere();
    } catch (e) { alert('Error releasing'); }
    setReleasing(null);
  };

  const EmpChip = ({ e, bg }) => {
    const ops = e.operations || [];
    return (
      <div style={{
        padding: '6px 8px', borderRadius: 6, marginBottom: 4, fontSize: 12,
        background: bg || '#F7F6F3', border: '0.5px solid rgba(0,0,0,0.10)'
      }}>
        <div style={{ fontWeight: 600, lineHeight: 1.2 }}>{e.name}</div>
        <div style={{ fontSize: 10, color: '#666666', marginBottom: ops.length ? 4 : 0 }}>{e.emp_id} · Line {e.line_number}</div>
        {ops.length > 0 && (
          <div style={{ fontSize: 10, color: '#666666', lineHeight: 1.4 }}>
            {ops.slice(0, 3).join(', ')}{ops.length > 3 ? ` +${ops.length - 3} more` : ''}
          </div>
        )}
      </div>
    );
  };

  const absent = poolData?.absent || [];
  const local  = poolData?.local  || [];
  const global = poolData?.global || [];

  return (
    <div style={{
      flex: '1 1 300px', maxWidth: 360, background: '#fff', borderRadius: 8,
      border: '0.5px solid rgba(0,0,0,0.10)', overflow: 'hidden',
      maxHeight: 560, display: 'flex', flexDirection: 'column',
      position: 'sticky', top: 16
    }}>
      <div style={{ padding: '12px 14px 10px', borderBottom: '0.5px solid rgba(0,0,0,0.10)', flexShrink: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Operator Pool</div>
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            placeholder="Employee code…" value={searchCode}
            onChange={e => setSearchCode(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
            style={{ flex: 1, fontSize: 13, padding: '8px 10px', borderRadius: 6, border: '1.5px solid rgba(0,0,0,0.15)', boxSizing: 'border-box' }}
          />
          <button onClick={handleSearch} style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: '#185FA5', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Go</button>
        </div>
        {searching && <div style={{ fontSize: 11, color: '#666666', marginTop: 6 }}>Searching…</div>}
        {searchResults && !searching && searchResults.length === 0 && (
          <div style={{ fontSize: 11, color: '#666666', marginTop: 6 }}>No match found</div>
        )}
        {searchResults && searchResults.map(r => (
          <div key={r.id} style={{ border: '0.5px solid rgba(0,0,0,0.10)', borderRadius: 7, padding: '8px 10px', marginTop: 6, background: r.assignment ? '#E6F1FB' : '#EAF3DE' }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>{r.name}</div>
            <div style={{ fontSize: 10.5, color: '#666666' }}>{r.emp_code} · Home: Line {r.home_line || '—'}</div>
            {r.assignment ? (
              <>
                <div style={{ fontSize: 11.5, marginTop: 4 }}>
                  On <strong>Line {r.assignment.line_number}</strong> · {r.assignment.operation_name}
                  {r.assignment.is_absent && <span style={{ color: '#A32D2D' }}> (marked absent)</span>}
                </div>
                <button onClick={() => handleRelease(r)} disabled={releasing === r.id}
                  style={{ marginTop: 6, width: '100%', padding: '8px 0', borderRadius: 6, border: '1.5px solid #A32D2D', color: '#A32D2D', background: '#FCEBEB', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                  {releasing === r.id ? 'Releasing…' : 'Release from there'}
                </button>
              </>
            ) : (
              <div style={{ fontSize: 11.5, color: '#3B6D11', marginTop: 4 }}>Not currently assigned — available</div>
            )}
          </div>
        ))}
      </div>
      <div style={{ overflowY: 'auto', flex: 1, padding: '10px 12px 14px' }}>
        {loading && <Loading text="Loading pool…" />}
        {!loading && poolData && <>
          {absent.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <SectionLabel>Absent today</SectionLabel>
              {absent.map(e => <EmpChip key={e.id} e={e} bg="#FCEBEB" />)}
            </div>
          )}
          <div style={{ marginBottom: 14 }}>
            <SectionLabel>Local pool — same line</SectionLabel>
            {local.length === 0 ? (
              <div style={{ fontSize: 11, color: '#666666', fontStyle: 'italic', padding: '4px 0' }}>
                No undeployed operators on this line
              </div>
            ) : local.map(e => <EmpChip key={e.id} e={e} />)}
          </div>
          <div style={{ height: 1, background: 'rgba(0,0,0,0.10)', margin: '4px 0 14px' }} />
          <div>
            <SectionLabel>Global pool — other lines</SectionLabel>
            {global.length === 0 ? (
              <div style={{ fontSize: 11, color: '#666666', fontStyle: 'italic', padding: '4px 0' }}>
                No available operators in the unit
              </div>
            ) : global.map(e => <EmpChip key={e.id} e={e} />)}
          </div>
        </>}
      </div>
    </div>
  );
}

// ─── Slot Chip ────────────────────────────────────────────────────────────────
function TempClubModal({ open, onClose, deploymentId, targetSlotId, targetOpName, operations, onDone }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const candidates = [];
  operations.forEach(op => {
    op.slots.forEach(s => {
      if (s.employee_id && !s.is_absent && !s.is_clubbed) {
        candidates.push({ slot_id: s.id, emp_name: s.emp_name, emp_id: s.emp_id, op_name: op.operation_name });
      }
    });
  });

  const confirm = async (candidate) => {
    setSaving(true); setError('');
    const r = await fetch(`${API}/daily-balancing/${deploymentId}/temp-club`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ slot_id_a: candidate.slot_id, slot_id_b: targetSlotId })
    });
    const data = await r.json();
    if (!r.ok) { setError(data.error || 'Could not create temporary club'); setSaving(false); return; }
    onDone(); onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title={`Temporary cover — ${targetOpName}`} width={440}>
      <div style={{ fontSize: 12, color: '#666666', marginBottom: 10 }}>
        Pick someone already working on this line — they'll cover both operations <strong>today only</strong>.
        This automatically reverts tomorrow.
      </div>
      {error && (
        <div style={{ background: '#FCEBEB', border: '1px solid #A32D2D', borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 12, color: '#A32D2D' }}>
          {error}
        </div>
      )}
      {candidates.length === 0 && <EmptyState message="No available operator on this line to share — everyone present is already assigned or clubbed." />}
      {candidates.map(c => (
        <div key={c.slot_id} onClick={() => !saving && confirm(c)} style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
          borderRadius: 7, border: '0.5px solid rgba(0,0,0,0.08)', marginBottom: 5,
          cursor: saving ? 'default' : 'pointer', background: '#fff', opacity: saving ? 0.6 : 1
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{c.emp_name}</div>
            <div style={{ fontSize: 11, color: '#666666' }}>{c.emp_id} · currently on {c.op_name}</div>
          </div>
        </div>
      ))}
    </Modal>
  );
}

function SlotChip({ slot, isSelected, onTap, onAbsent, onPresent, onAssign, onTempClub, onUndoTempClub, onDragStart, onDragOver, onDrop }) {
  const isEmpty = slot.is_vacant || !slot.employee_id;
  const canSwap = !isEmpty && !slot.is_clubbed;
  return (
    <div
      draggable={canSwap}
      onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop}
      onClick={canSwap ? onTap : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        border: isSelected ? '2px solid #185FA5' : tierBorderColor(slot), borderRadius: 7,
        padding: '9px 10px',
        background: isSelected ? '#E6F1FB' : isEmpty ? '#FCEBEB' : slot.is_absent ? '#fff8f0' : '#fff',
        minWidth: 150, cursor: isEmpty ? 'default' : slot.is_clubbed ? 'default' : 'pointer', fontSize: 12, userSelect: 'none',
        boxShadow: isSelected ? '0 0 0 3px rgba(24,95,165,0.15)' : 'none'
      }}
    >
      {isEmpty ? (
        <span style={{ color: '#A32D2D', fontStyle: 'italic', flex: 1 }}>Vacant</span>
      ) : (
        <>
          {slot.is_clubbed && (
            <span title={slot.temp_club_date ? 'Temporary — reverts tomorrow' : 'Linked to clubbed operation'} style={{ fontSize: 11, flexShrink: 0 }}>
              {slot.temp_club_date ? '🤝' : '🔗'}
            </span>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {slot.emp_name}
            </div>
            <div style={{ fontSize: 10, color: '#666666' }}>{slot.emp_id}</div>
          </div>
          {slot.is_absent && <Badge type="red" size="xs">Absent</Badge>}
          {!slot.is_absent && tierBadge(slot.match_tier)}
          <Badge type={skillBadgeType(parseFloat(slot.skill_pct) || 0)}>
            {Math.round(parseFloat(slot.skill_pct) || 0)}%
          </Badge>
        </>
      )}
      <div style={{ display: 'flex', gap: 4, marginLeft: 2, flexShrink: 0 }}>
        {!isEmpty && !slot.is_absent && (
          <button onClick={(e) => { e.stopPropagation(); onAbsent(); }} title="Mark absent" className="btn btn-s"
            style={{ padding: '6px 10px', minWidth: 30, minHeight: 30, color: '#A32D2D', borderColor: '#FCEBEB', background: '#FCEBEB', fontSize: 13 }}>
            ✕
          </button>
        )}
        {slot.is_absent && (
          <button onClick={(e) => { e.stopPropagation(); onPresent(); }} title="Mark present" className="btn btn-s"
            style={{ padding: '6px 10px', minWidth: 30, minHeight: 30, color: '#3B6D11', borderColor: '#EAF3DE', background: '#EAF3DE', fontSize: 13 }}>
            ↩
          </button>
        )}
        {(isEmpty || slot.is_absent) && !slot.temp_club_date && (
          <button onClick={(e) => { e.stopPropagation(); onTempClub(); }} title="Temporarily cover from another operation (today only)" className="btn btn-s"
            style={{ padding: '6px 10px', minWidth: 30, minHeight: 30, color: '#B8860B', borderColor: '#FCEFD0', background: '#FCEFD0', fontSize: 13 }}>
            🤝
          </button>
        )}
        {slot.is_clubbed && slot.temp_club_date && (
          <button onClick={(e) => { e.stopPropagation(); onUndoTempClub(); }} title="Undo temporary club" className="btn btn-s"
            style={{ padding: '6px 10px', minWidth: 30, minHeight: 30, color: '#B8860B', borderColor: '#FCEFD0', background: '#FCEFD0', fontSize: 13 }}>
            ↺
          </button>
        )}
        <button onClick={(e) => { e.stopPropagation(); onAssign(); }} title="Reassign" className="btn btn-s"
          style={{ padding: '6px 10px', minWidth: 30, minHeight: 30, color: '#185FA5', borderColor: '#E6F1FB', background: '#E6F1FB', fontSize: 13 }}>
          ↔
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function DailyBalancing() {
  const [deployments, setDeployments] = useState([]);
  const [depId, setDepId] = useState('');
  const [dep, setDep] = useState(null);
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [assignModal, setAssignModal] = useState({ open: false, slotId: null, ldiId: null, opName: '', isClubbed: false, clubbedOpName: '', clubbedLineNumber: null });
  const [tempClubModal, setTempClubModal] = useState({ open: false, slotId: null, opName: '' });
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [poolKey, setPoolKey] = useState(0);
  const dragSlotId = useRef(null);
  const [selectedSlotId, setSelectedSlotId] = useState(null);

  useEffect(() => {
    const el = document.getElementById('lc-page-title');
    if (el) el.textContent = 'Daily Balancing';
  }, []);

  useEffect(() => {
    fetch(`${API}/deployment`, { headers: authHeaders() })
      .then(async r => {
        const data = await r.json();
        if (!r.ok) { setFetchError(`Error ${r.status}: ${data.error || r.statusText}`); return; }
        const active = (Array.isArray(data) ? data : []).filter(x => x.status?.toLowerCase() === 'active');
        setDeployments(active);
      })
      .catch(err => setFetchError(`Network error: ${err.message}`));
  }, []);

  const loadDeployment = useCallback((id) => {
    if (!id) return;
    setLoading(true);
    setFetchError('');
    fetch(`${API}/daily-balancing/${id}`, { headers: authHeaders() })
      .then(async r => {
        const d = await r.json();
        if (!r.ok) { setFetchError(`Error: ${d.error}`); setLoading(false); return; }
        setDep(d);
        setOperations(groupSlots(d.slots || []));
        setLoading(false);
      })
      .catch(err => { setFetchError(`Network error: ${err.message}`); setLoading(false); });
  }, []);

  const refresh = useCallback(() => {
    if (depId) { loadDeployment(depId); setPoolKey(k => k + 1); }
  }, [depId, loadDeployment]);

  const handleSelect = (id) => {
    setDepId(id); loadDeployment(id); setPoolKey(k => k + 1);
  };

  const handleBack = () => {
    setDep(null); setDepId(''); setOperations([]);
    const el = document.getElementById('lc-page-title');
    if (el) el.textContent = 'Daily Balancing';
  };

  const handleAbsent = async (slotId) => {
    await fetch(`${API}/daily-balancing/${depId}/mark-absent`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ slot_id: slotId })
    });
    refresh();
  };

  const handleUndoTempClub = async (slotId) => {
    if (!window.confirm('Undo this temporary club? Both operations go back to their own status.')) return;
    await fetch(`${API}/daily-balancing/${depId}/temp-unclub`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ slot_id: slotId })
    });
    refresh();
  };

  const handlePresent = async (slotId) => {
    await fetch(`${API}/daily-balancing/${depId}/mark-present`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ slot_id: slotId })
    });
    refresh();
  };

  const handleAddSlot = async (ldiId) => {
    await fetch(`${API}/daily-balancing/${depId}/slots`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ line_design_item_id: ldiId })
    });
    refresh();
  };

  const handleRemoveSlot = async (op) => {
    if (op.slots.length <= 1) return;
    const last = op.slots[op.slots.length - 1];
    await fetch(`${API}/daily-balancing/${depId}/slots/${last.id}`, { method: 'DELETE', headers: authHeaders() });
    refresh();
  };

  const handleDragStart = (slotId) => { dragSlotId.current = slotId; };
  const handleDrop = async (targetSlotId) => {
    if (!dragSlotId.current || dragSlotId.current === targetSlotId) { dragSlotId.current = null; return; }
    await fetch(`${API}/daily-balancing/${depId}/slots/swap`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ slot_a: dragSlotId.current, slot_b: targetSlotId })
    });
    dragSlotId.current = null;
    refresh();
  };

  // Touch-friendly alternative to drag-and-drop: tap one slot to select it,
  // tap a second slot to swap. Uses the exact same swap endpoint as drag-drop.
  const handleSlotTap = (slotId) => {
    if (selectedSlotId === null) {
      setSelectedSlotId(slotId);
    } else if (selectedSlotId === slotId) {
      setSelectedSlotId(null);
    } else {
      dragSlotId.current = selectedSlotId;
      handleDrop(slotId);
      setSelectedSlotId(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const r = await fetch(`${API}/daily-balancing/${depId}/save`, {
      method: 'POST', headers: authHeaders()
    });
    const data = await r.json();
    setSaving(false);
    if (r.ok) {
      setSavedMsg('Saved ✓');
      setTimeout(() => setSavedMsg(''), 2000);
    } else {
      setSavedMsg(`Error: ${data.error}`);
      setTimeout(() => setSavedMsg(''), 4000);
    }
  };

  const efficiency = dep ? calcEfficiency(operations, dep.total_sam, dep.operating_minutes) : 0;
  const deployedBottleneck = dep ? calcBottleneck(operations) : null;
  const operationsWithBottleneck = operations.map(op => ({
    ...op,
    is_deployed_bottleneck: op.operation_name === deployedBottleneck
  }));
  // "Deployed" should reflect real headcount — a shared operator has 2 slot rows
  // (one per operation) but is 1 real person.
  const deployed = new Set(
    operations.flatMap(op => op.slots.filter(sl => sl.employee_id && !sl.is_absent).map(sl => sl.employee_id))
  ).size;
  // A vacant clubbed pair is 2 slot rows but 1 missing real position.
  const vacant = operations.reduce((s, op) =>
    s + op.slots.filter(sl => sl.is_vacant || !sl.employee_id).reduce((sum, sl) => sum + slotWeight(sl), 0), 0);
  const skillGaps = operations.filter(op => {
    const present = op.slots.filter(s => s.employee_id && !s.is_absent);
    if (!present.length) return true;
    const w = present.reduce((a, s) => a + slotWeight(s), 0);
    return (present.reduce((a, s) => a + parseFloat(s.skill_pct || 0) * slotWeight(s), 0) / w) < op.skill_req_pct;
  }).length;
  const absentCount = operations.reduce((s, op) => s + op.slots.filter(sl => sl.is_absent).length, 0);
  // Possible output/hr — the line's actual achievable rate today, constrained
  // by whichever operation is the real bottleneck given current staffing.
  const possibleOutputHr = operations.length
    ? Math.round(Math.min(...operations.map(op => {
        const present = op.slots.filter(s => s.employee_id && !s.is_absent);
        if (!present.length) return 0;
        return present.reduce((sum, s) => {
          const sk = parseFloat(s.skill_pct || 0);
          if (!sk) return sum;
          return sum + slotWeight(s) * (60 / (parseFloat(op.sam) / (sk / 100)));
        }, 0);
      })))
    : 0;

  // ── Landing — show line cards ──
  if (!dep && !loading) {
    return <LineSelector deployments={deployments} onSelect={handleSelect} error={fetchError} />;
  }

  // ── Loading ──
  if (loading) {
    return (
      <div style={{ padding: 16, background: '#EFEDE8', minHeight: '100vh' }}>
        <Loading />
      </div>
    );
  }

  // ── Detail view ──
  return (
    <div style={{ padding: 16, background: '#EFEDE8', minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '1 1 auto', minWidth: 0 }}>
          <button onClick={handleBack} className="btn btn-s" style={{ color: '#185FA5', borderColor: '#E6F1FB', background: '#E6F1FB', flexShrink: 0 }}>
            ← Lines
          </button>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 700 }}>Daily Balancing</div>
            <div style={{ fontSize: 12, color: '#666666', marginTop: 2 }}>
              Line {dep.line_number} · {dep.shift_name} · {dep.style_number}
              {dep.buyer ? ` — ${dep.buyer}` : ''}
            </div>
          </div>
        </div>
        <Btn variant="primary" size="sm" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : savedMsg || 'Save'}
        </Btn>
      </div>

      {/* Error banner */}
      {fetchError && (
        <div style={{ background: '#FCEBEB', border: '1px solid #A32D2D', borderRadius: 7, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#A32D2D' }}>
          {fetchError}
        </div>
      )}

      {/* Touch swap hint */}
      {selectedSlotId && (
        <div style={{ background: '#E6F1FB', border: '1px solid #185FA5', borderRadius: 7, padding: '9px 14px', marginBottom: 14, fontSize: 12.5, color: '#0C447C', fontWeight: 500 }}>
          Tap another operator to swap — or tap the highlighted one again to cancel.
        </div>
      )}

      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(115px, 1fr))', gap: 10, marginBottom: 18 }}>
        <KpiCard label="DB Efficiency" value={`${efficiency}%`}
          type={efficiency >= 75 ? 'green' : efficiency >= 65 ? 'amber' : 'red'} />
        <KpiCard label="Line Design Eff." value={`${Math.round(dep?.plan_efficiency || dep?.efficiency || 0)}%`}
          type="blue" />
        <KpiCard label="Deployed" value={deployed} type="blue" />
        <KpiCard label="Vacant" value={Math.round(vacant)} type={vacant > 0 ? 'red' : 'green'} />
        <KpiCard label="Skill gaps" value={skillGaps} type={skillGaps > 0 ? 'amber' : 'green'} />
        <KpiCard label="Absent today" value={absentCount} type={absentCount > 0 ? 'red' : 'default'} />
        <KpiCard label="Possible Output/hr" value={possibleOutputHr} type="blue" />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-start' }}>

        {/* Operations */}
        <div style={{ flex: '3 1 480px', minWidth: 0 }}>
          {operations.map(op => {
            const presentSlots = op.slots.filter(s => s.employee_id && !s.is_absent);
            const opOutputHr = presentSlots.length > 0
              ? presentSlots.reduce((sum, s) => {
                  const sk = parseFloat(s.skill_pct || 0);
                  if (!sk) return sum;
                  return sum + slotWeight(s) * (60 / (parseFloat(op.sam) / (sk / 100)));
                }, 0)
              : 0;
            // Use Line Design's own bottleneck output/hr (static E9 reference, not
            // recalculated from deployment) as the denominator for Available skill%.
            const ldBottleneckHr = Math.min(...operations.map(o => parseFloat(o.output_per_hr) || Infinity));
            const avgSkill = (ldBottleneckHr > 0 && ldBottleneckHr !== Infinity && opOutputHr > 0)
              ? Math.round((opOutputHr / ldBottleneckHr) * 100)
              : 0;
            const hasGap = presentSlots.length === 0 || opOutputHr < ldBottleneckHr * 0.99;

            return (
              <div key={op.line_design_item_id} style={{
                background: '#fff', borderRadius: 8, marginBottom: 8, overflow: 'hidden',
                border: op.is_bottleneck ? '1.5px solid #A32D2D' : '0.5px solid rgba(0,0,0,0.10)'
              }}>
                <div style={{
                  display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 8px', padding: '10px 14px',
                  background: op.is_bottleneck ? '#FCEBEB' : '#F7F6F3'
                }}>
                  <span style={{ fontWeight: 600, fontSize: 13.5, flex: '1 1 100%' }}>{op.operation_name}</span>
                  {op.is_clubbed && (
                    op.slots.some(s => s.temp_club_date) ? (
                      <Badge type="amber" size="xs">🤝 Temp cover today</Badge>
                    ) : op.clubbed_operation_name && op.clubbed_operation_name !== op.operation_name ? (
                      <Badge type="blue" size="xs">🔗 Clubbed with {op.clubbed_operation_name}</Badge>
                    ) : (
                      <Badge type="blue" size="xs">🔗 Shared across {op.slots.length} slots</Badge>
                    )
                  )}
                  {op.is_deployed_bottleneck && <Badge type="red" size="xs">⚠ Bottleneck</Badge>}
                  <span style={{ fontSize: 11, color: '#666666' }}>SAM {parseFloat(op.sam).toFixed(3)}</span>
                  <span style={{ fontSize: 11, color: '#666666' }}>Output {Math.round(opOutputHr)}/hr</span>
                  <Badge type={skillBadgeType(op.skill_req_pct)}>Req {op.skill_req_pct}%</Badge>
                  {presentSlots.length > 0 ? (
                    <Badge type={hasGap ? 'red' : 'green'}>Avg {avgSkill}% {hasGap ? '↓' : '✓'}</Badge>
                  ) : (
                    <Badge type="red">No present ops</Badge>
                  )}
                  {!op.is_clubbed && (
                    <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
                      <button onClick={() => handleRemoveSlot(op)} disabled={op.slots.length <= 1}
                        className="btn btn-s" style={{
                          color: op.slots.length <= 1 ? '#bbb' : '#A32D2D',
                          background: op.slots.length <= 1 ? '#f5f5f5' : '#FCEBEB',
                          borderColor: 'transparent', padding: '6px 14px', minHeight: 30, fontWeight: 700, fontSize: 14
                        }}>−</button>
                      <button onClick={() => handleAddSlot(op.line_design_item_id)}
                        className="btn btn-s" style={{
                          color: '#3B6D11', background: '#EAF3DE',
                          borderColor: 'transparent', padding: '6px 14px', minHeight: 30, fontWeight: 700, fontSize: 14
                        }}>+</button>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '10px 14px' }}>
                  {op.slots.map(slot => (
                    <SlotChip
                      key={slot.id}
                      slot={slot}
                      isSelected={selectedSlotId === slot.id}
                      onTap={() => handleSlotTap(slot.id)}
                      onAbsent={() => handleAbsent(slot.id)}
                      onPresent={() => handlePresent(slot.id)}
                      onAssign={() => setAssignModal({
                        open: true, slotId: slot.id, ldiId: op.line_design_item_id, opName: op.operation_name,
                        isClubbed: slot.is_clubbed, clubbedOpName: slot.clubbed_operation_name,
                        clubbedLineNumber: slot.clubbed_line_number
                      })}
                      onTempClub={() => setTempClubModal({ open: true, slotId: slot.id, opName: op.operation_name })}
                      onUndoTempClub={() => handleUndoTempClub(slot.id)}
                      onDragStart={() => handleDragStart(slot.id)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => handleDrop(slot.id)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Pool */}
        <PoolPanel key={poolKey} deploymentId={depId} onReleaseElsewhere={refresh} />
      </div>

      <AssignModal
        open={assignModal.open}
        onClose={() => setAssignModal({ open: false, slotId: null, ldiId: null, opName: '', isClubbed: false, clubbedOpName: '', clubbedLineNumber: null })}
        deploymentId={depId}
        ldiId={assignModal.ldiId}
        slotId={assignModal.slotId}
        opName={assignModal.opName}
        isClubbed={assignModal.isClubbed}
        clubbedOpName={assignModal.clubbedOpName}
        clubbedLineNumber={assignModal.clubbedLineNumber}
        currentLineNumber={dep?.line_number}
        onAssigned={refresh}
      />

      <TempClubModal
        open={tempClubModal.open}
        onClose={() => setTempClubModal({ open: false, slotId: null, opName: '' })}
        deploymentId={depId}
        targetSlotId={tempClubModal.slotId}
        targetOpName={tempClubModal.opName}
        operations={operations}
        onDone={refresh}
      />
    </div>
  );
}